import React from "react";
import { Box, HStack, Text, Icon, IconButton, Button, Tooltip, keyframes } from "@chakra-ui/react";
import { AlertTriangle, Phone, PhoneIncoming, PhoneOutgoing, PhoneOff } from "lucide-react";
import { useSoftphone, STATUS_FINAIS } from "../contexts/softphone";

const pulseBg = keyframes`
    0%   { background-color: #b91c1c; }
    50%  { background-color: #dc2626; }
    100% { background-color: #b91c1c; }
`;

const fmtDuration = (sec) => {
    if (!sec && sec !== 0) return "00:00";
    const m = Math.floor(sec / 60).toString().padStart(2, "0");
    const s = (sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
};

const fmtNumero = (raw) => {
    const n = String(raw || "").replace(/\D/g, "");
    if (n.length === 11) return `(${n.slice(0,2)}) ${n.slice(2,7)}-${n.slice(7)}`;
    if (n.length === 10) return `(${n.slice(0,2)}) ${n.slice(2,6)}-${n.slice(6)}`;
    return raw || "";
};

const ActiveCallBanner = () => {
    const sp = useSoftphone();
    const call = sp.call;
    const inCall = !!call && !STATUS_FINAIS.includes(call.status);
    if (!inCall) return null;

    const incoming = call.direction === "incoming" && !call.answeredAt;
    const ativo = !!call.answeredAt;
    const DirIcon = call.direction === "incoming" ? PhoneIncoming : PhoneOutgoing;
    const nome = sp.activeCliente?.nome || fmtNumero(call.remote) || call.remote || "Chamada";

    return (
        <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            zIndex={5000}
            color="white"
            py={2}
            px={4}
            animation={`${pulseBg} 2s infinite`}
            boxShadow="0 4px 12px rgba(0,0,0,0.25)"
        >
            <HStack spacing={4} justify="space-between" align="center" maxW="1400px" mx="auto">
                <HStack spacing={3}>
                    <Box
                        bg="white"
                        color="red.600"
                        borderRadius="full"
                        w="32px"
                        h="32px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Icon as={AlertTriangle} boxSize={5} />
                    </Box>
                    <Box>
                        <Text fontSize="sm" fontWeight="extrabold" letterSpacing="wide" textTransform="uppercase">
                            {incoming ? "Chamada chegando" : ativo ? "Chamada em andamento" : "Discando"}
                        </Text>
                        <Text fontSize="xs" opacity={0.9}>
                            {incoming
                                ? "Clique em Atender. NÃO atualize nem feche esta aba — a chamada será encerrada."
                                : "NÃO atualize, feche ou navegue para fora desta aba — a chamada será encerrada."}
                        </Text>
                    </Box>
                </HStack>
                <HStack spacing={4}>
                    <HStack spacing={2}>
                        <DirIcon size={16} />
                        <Box>
                            <Text fontSize="xs" opacity={0.85} lineHeight="1">
                                {call.direction === "incoming" ? "DE" : "PARA"}
                            </Text>
                            <Text fontSize="sm" fontWeight="bold" lineHeight="1.1">{nome}</Text>
                        </Box>
                    </HStack>
                    {ativo && (
                        <Box bg="rgba(0,0,0,0.25)" px={3} py={1} borderRadius="md">
                            <Text fontSize="xs" opacity={0.85} lineHeight="1">DURAÇÃO</Text>
                            <Text fontSize="md" fontFamily="mono" fontWeight="bold" lineHeight="1.1">
                                {fmtDuration(call.durationSec)}
                            </Text>
                        </Box>
                    )}
                    {incoming ? (
                        // Atender direto no banner: ele é o único elemento garantido na
                        // tela (fixed/top, z 5000). O widget flutuante pode estar fora da
                        // área visível e a tela /discador só mostra o painel na 1ª aba.
                        <HStack spacing={2}>
                            <Button
                                size="sm"
                                colorScheme="green"
                                fontWeight="bold"
                                leftIcon={<Phone size={16} />}
                                onClick={sp.answer}
                            >
                                Atender
                            </Button>
                            <Button
                                size="sm"
                                colorScheme="whiteAlpha"
                                bg="rgba(0,0,0,0.3)"
                                color="white"
                                _hover={{ bg: "rgba(0,0,0,0.5)" }}
                                leftIcon={<PhoneOff size={16} />}
                                onClick={sp.hangup}
                            >
                                Recusar
                            </Button>
                        </HStack>
                    ) : (
                        <Tooltip label="Encerrar chamada">
                            <IconButton
                                size="sm"
                                colorScheme="whiteAlpha"
                                bg="rgba(0,0,0,0.3)"
                                color="white"
                                _hover={{ bg: "rgba(0,0,0,0.5)" }}
                                icon={<PhoneOff size={16} />}
                                aria-label="encerrar"
                                onClick={sp.hangup}
                            />
                        </Tooltip>
                    )}
                </HStack>
            </HStack>
        </Box>
    );
};

export default ActiveCallBanner;
